import { useEffect, useState } from "react";

import { Input, LoadingShimmer } from "@newsela/angelou";

import { ArticleLevel } from "@/types/models/ArticleLevel.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import {
  headingCss,
  inputCss,
  inputWrapperCss,
  loadingWrapperCss,
} from "./AssignmentTitleInput.style";

interface AssignmentTitleInputProps {
  currentLevel?: ArticleLevel;
  dispatch: () => void;
  handleTitleChange: (title: string, dispatch: () => void) => void;
  handleSetInitialAssignmentRef: (assignment: Assignment) => void;
  isLoading: boolean;
  isSavedAssignment: boolean;
  initialTitle?: string;
  updatedAssignment?: Assignment;
}

const AssignmentTitleInput = ({
  isLoading,
  isSavedAssignment,
  updatedAssignment,
  handleTitleChange,
  dispatch,
  handleSetInitialAssignmentRef,
  currentLevel,
  initialTitle,
}: AssignmentTitleInputProps) => {
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!updatedAssignment?.title) {
      if (updatedAssignment) {
        handleSetInitialAssignmentRef(updatedAssignment);
      }
    }
  }, [currentLevel?.id]);

  useEffect(() => {
    if (!isSavedAssignment) {
      const assignmentTitle = currentLevel?.title || initialTitle;
      // If a user hasn't updated the title or touched the input, change the title based on the current level.
      if (assignmentTitle && !touched)
        handleTitleChange(assignmentTitle, dispatch);
    }
  }, [currentLevel?.id]);

  const title = updatedAssignment?.title;
  const shouldShowError = touched && !title;
  const errorMessage = shouldShowError
    ? "An assignment must have a title."
    : "";

  const handleInputBlur = () => {
    if (!touched) {
      setTouched(true);
    }
  };

  const label = "Assignment title";

  if (isLoading) {
    return (
      <>
        <h3 css={headingCss}>{label}</h3>
        <div css={loadingWrapperCss}>
          <LoadingShimmer />
        </div>
      </>
    );
  }

  return (
    <div css={inputWrapperCss}>
      <Input
        dataProps={{
          "data-qa-selector": "assignment-title-input",
        }}
        error={errorMessage}
        inputType={Input.inputTypes.text}
        label={label}
        name="assignmentTitle"
        onBlur={handleInputBlur}
        touched={touched}
        onChange={(e) => {
          handleTitleChange(e.target.value, dispatch);
        }}
        underInputId="assignment-title-input"
        value={title ?? ""}
        __cssFor={{ input: inputCss }}
      />
    </div>
  );
};

export default AssignmentTitleInput;
